import { put, select, call, take, race, takeEvery, takeLatest } from 'redux-saga/effects';
import { delay } from 'redux-saga';

import equal from 'deep-equal';
import clone from 'clone-deep';

import {
  blank,
  dig,
  getQueryParameterByName,
  removeBlank,
  setUnlessDefined,
  uniqueBy,
} from 'spiceutils';

import FFVis from 'ff-vis-js/dist/FFVis';

import env from 'environment';

import {
  getListOfCountrySpecificBreaks,
} from 'utils/AYDAUtils';

import buildUrl from 'utils/buildUrl';

import {
  GET_QUESTION_SUGGESTIONS,
  CREATE_QUESTION_CONTROL,
  CREATE_CHART_CONTROL,
  GRAPHQL_URL,
  TYPEAHEAD_LIMIT,
  IS_AUTHENTICATED,
  GET_PACKAGE,
  GET_USER,
  GET_EXTERNAL_CDF,
  PLATFORM_LOGIN_URL,
  PLATFORM_URL,
  AYDA_UI_HOSTNAME,
  PPTX_DOWNLOAD_START,
  PPTX_DOWNLOAD_STOP,
  PPTX_DOWNLOAD_URL,
  PPTX_DOWNLOAD_FAILED,
  PPTX_DOWNLOAD_READY,
  GET_PRESET_COUNTRY_LIST,
  GET_PRESET_BREAK_LIST,
  NEW_CHART,
  CREATE_TEMPORARY_CHART_CONTROL,
  AUTHORISED_ROLES,
} from './constants';

import {
  updateQuestionSuggestions,
  storeQuestionControlList,
  storeTemporaryQuestionControlList,
  storeMetaJSON,
  setMetaData,
  setControlList,
  setPackage,
  setUser,
  setOrg,
  setRole,
  storeNewChart,
  getExternalCdf,
  getPptxDownloadSuccessAction,
  getPptxDownloadFailureAction,
  storePresetClusterLists,
  setPresetClusterControl,
  newChart,
  setShowNewQuestion,
  setPageLoading,
} from './actions';

import {
  makeSelectCDF,
  makeSelectMetaData,
  makeSelectMetaJSON,
  makeSelectClientPackage,
  makeSelectControlList,
  makeSelectQuestionControlList,
} from './selectors';

export function* getQuestionSelections() {
  yield takeEvery(GET_QUESTION_SUGGESTIONS, fetchQuestionSelections);
}

/*
 * Watch for PPTX download actions and loop until PPTX Download is ready
 */
export function* getPptxDownload() {
  // eslint-disable-next-line no-constant-condition
  while (true) {
    const action = yield take(PPTX_DOWNLOAD_START);
    yield race({
      task: call(fetchPptxDownload, action),
      cancel: take([PPTX_DOWNLOAD_READY, PPTX_DOWNLOAD_STOP, PPTX_DOWNLOAD_FAILED]),
    });
  }
}

export function* makeQuestionControl() {
  yield takeLatest(CREATE_QUESTION_CONTROL, createQuestionControlSaga);
}

export function* makeChartControl() {
  yield takeLatest(CREATE_CHART_CONTROL, createChartControlSaga);
}

export function* makeTemporaryChartControl() {
  yield takeLatest(CREATE_TEMPORARY_CHART_CONTROL, createTempChartControlSaga);
}

export function* setNewChart() {
  yield takeEvery(NEW_CHART, setNewChartSaga);
}

export function* fetchPresetCountryList() {
  yield takeEvery(GET_PRESET_COUNTRY_LIST, getPresetCountryListSaga);
}

export function* fetchPresetBreakList() {
  yield takeEvery(GET_PRESET_BREAK_LIST, getPresetBreakListSaga);
}

export function* isAuthenticated() {
  yield takeEvery(IS_AUTHENTICATED, checkIsAuth);
}

export function* fetchExternalCdf() {
  yield takeEvery(GET_EXTERNAL_CDF, fetchExternalCdfSaga);
}

export function* fetchExternalPackage() {
  yield takeEvery(GET_PACKAGE, fetchExternalPackageSaga);
}

export function* fetchExternalUser() {
  yield takeEvery(GET_USER, fetchExternalUserSaga);
}

export function* checkIsAuth(params) {
  const role = yield graphQl(`
    {
      currentRole
    }
  `);
  const contentId = getQueryParameterByName('contentId');
  let cdf = getQueryParameterByName('cdf');
  if (cdf) {
    try {
      cdf = JSON.parse(cdf);
    } catch (e) { cdf = null; }
  }
  let userRole;
  let authenticated;
  let canViewAdvancedTool = false;

  if (role.error) {
    // There was an error getting the role. GraphQL could be down or another error.
    // Pass through auth logic but set role to 'auth_error' so that we can display an error
    // message to the user.
    authenticated = true;
    userRole = 'auth_error';
  } else {
    userRole = role.currentRole;
    authenticated = AUTHORISED_ROLES.includes(role.currentRole);
    canViewAdvancedTool = role.currentRole === 'ff_editor';
  }

  if (env.NODE_ENV === 'development') {
    authenticated = true;
    canViewAdvancedTool = true;
    userRole = 'ff_editor';
  }
  yield put(setRole(userRole));

  // If the role returned from graphql was not an authenticated role then redirect to nvision
  // Send the contentId along if it is present.
  if (!authenticated) {
    window.location.replace(buildUrl(PLATFORM_LOGIN_URL, { callback: 'datatool', contentId, cdf: cdf && JSON.stringify(cdf) }));
  } else if (!canViewAdvancedTool && (params.path === '/advanced' || params.path === '/search' /* Disable codebook for non-admins */)) {
    // If the user is not an editor and they are trying to access the advanced tool, redirect to
    // the slim version.
    document.location.href = '/';
  }

  // We are authenticated, grab the CDF if we have a Content ID present
  if (contentId) {
    yield put(getExternalCdf(contentId));
  } else if (cdf) {
    yield put(newChart(cdf));
  } else {
    yield put(setPageLoading(false));
  }
}

export function* fetchExternalPackageSaga() {
  const ffPackage = yield graphQl(`
    {
      currentPackage
    }
  `);
  if (ffPackage.currentPackage) {
    yield put(setPackage(ffPackage.currentPackage));
  }
}

export function* fetchExternalUserSaga() {
  const ffUser = yield graphQl(`
    {
      currentUser
      currentOrgId
    }
  `);
  if (ffUser.currentUser) {
    yield put(setUser(ffUser.currentUser));
  }
  if (ffUser.currentOrgId) {
    yield put(setOrg(ffUser.currentOrgId));
  }
}

export function* fetchExternalCdfSaga(params) {
  const response = yield Fetch(`${PLATFORM_URL}/datatoolapi/v1/contents/${params.contentId}/cdf`, {
    method: 'get',
    headers: {
      Accept: 'application/json',
      'Content-Type': 'application/json',
    },
  });
  const cdf = yield response.json();
  yield put(newChart(cdf));
}

export function* fetchQuestionSelections(action) {
  let questions = yield graphQl(`
    {
      fnQuestionTitleSearch(query:"${action.value}", first:${TYPEAHEAD_LIMIT}){
        nodes{
          questionCode
          title
        }
      }
    }
  `);
  questions = questions.fnQuestionTitleSearch.nodes;
  questions = uniqueBy(questions, 'questionCode');
  yield put(updateQuestionSuggestions(questions));
}

export function* createQuestionControlSaga(action) {
  if (action.code === '') {
    yield put(storeQuestionControlList({}));
  } else {
    const qcl = yield initQuestionControlList(action.code);
    yield put(storeQuestionControlList(qcl));
  }
}

function* initQuestionControlList(code, metaData = null) {
  const clientPackage = yield select(makeSelectClientPackage());
  let metaJSON = metaData;
  if (blank(metaJSON)) {
    metaJSON = yield select(makeSelectMetaData(code));
  }
  if (blank(metaJSON)) {
    metaJSON = yield getMetaJSON(code);
  }
  const qcc = new FFVis.QuestionControlConfig(code, clientPackage, metaJSON);
  yield put(setMetaData(qcc.questionMeta, code));
  return yield qcc.getQuestionControlList('promise');
}

export function* createChartControlSaga(action) {
  const ccl = yield initChartControlList(action);
  yield put(setControlList(ccl));
}

// Used by new question selector so we don't modify the global CL
export function* createTempChartControlSaga(action) {
  const ccl = yield initChartControlList(action);
  yield put(storeTemporaryQuestionControlList(ccl));
}

/*
 * Expects action to be object with key/values
 * - visId: String of form 'SlideX' where x is an integer
 * - code: String containing a question code (e.g.: "Sex_orient")
 * - kidsWave: Boolean, true for kids data only, false for adults data
 */
function* initChartControlList(action) {
  const { visId, code, kidsWave } = action;

  const ccl = yield createChartControlList(visId, code, kidsWave);

  let qcl = yield select(makeSelectQuestionControlList());

  if (blank(qcl) || qcl.questionCode !== code) {
    qcl = yield initQuestionControlList(code, ccl.Metadata);
  }

  // Find visualisation with name equal to visId
  const visFromQuestionControl = qcl.controlList.VisualisationList.Options[kidsWave ? 'kidList' : 'adultList'].filter((vis) => vis.name === visId)[0];

  if (visFromQuestionControl) {
    // Extract necessary fields from visualisation
    const {
      slideAvailable,
      pptxAvailable,
      csvAvailable,
    } = visFromQuestionControl;

    // Create visualisation "control" in list with extracted fields
    ccl.Visualisation = {
      slideAvailable,
      pptxAvailable,
      csvAvailable,
    };
  }

  return ccl;
}

export function* setNewChartSaga(action) {
  const cdf = clone(action.cdf);
  const { Visualisation, QuestionCode } = cdf;
  const kidsWave = blank(cdf.Adults) ? false : !cdf.Adults;

  const ccl = yield initChartControlList({ visId: Visualisation, kidsWave, code: QuestionCode });

  if (dig(ccl.BreakList, ['Visible']) === true) {
    yield getPresetBreakListSaga({ country: cdf.Country, breakList: cdf.BreakList, controlList: ccl, kidsWave: blank(cdf.Adults) ? false : !cdf.Adults });
  }
  if (dig(ccl.CountryList, ['Visible']) === true) {
    yield getPresetCountryListSaga({ countryList: cdf.CountryList });
  }

  const { fnVisSwitchPossible: respondToBreakGroupChange } = yield graphQl(`{
    fnVisSwitchPossible(visId: ${Visualisation.replace(/^Slide/, '')})
  }`);

  /**
   * Remove entries from a 2D array based on a filter function.
   * Removes empty arrays from the resulting list.
   * @template T
   * @param {T[][]} list
   * @param {(item: T, index: number, array: T[]) => boolean} fn
   */
  const filterList = (list, fn) => (
    list.map((entry) => entry.filter(fn)).filter((entry) => entry.length > 0)
  );

  // Filter out unavailable countries and breaks
  if (dig(ccl, 'CountryList', 'Visible') && !blank(cdf.CountryList)) {
    cdf.CountryList = filterList(cdf.CountryList, (country) => (
      ccl.CountryList.Options.AllSurveys.includes(country)
    ));
  }
  if (dig(ccl, 'BreakList', 'Visible') && !blank(cdf.BreakList)) {
    cdf.BreakList = filterList(cdf.BreakList, (breakEntry) => {
      const breakGroupName = Object.keys(breakEntry)[0];
      const cclEntry = ccl.BreakList.Options.AllSurveys.find((cclEntry) => breakGroupName in cclEntry);
      return cclEntry !== undefined;
    });
  }

  yield put(storeNewChart(cdf, ccl, respondToBreakGroupChange));

  yield put(setShowNewQuestion(false));
  yield put(setPageLoading(false));
}

export function* getPresetCountryListSaga(action) {
  const data = yield graphQl(`
    {
      fnGetAllPackageCountrylists {
        nodes{
          packageId
          listName
          countryLists {
            countryNames
          }
        }
      }
    }
  `);

  const presetLists = data.fnGetAllPackageCountrylists.nodes.map(({ listName, countryLists }) => ({
    name: listName,
    countryList: countryLists.map(({ countryNames }) => countryNames),
  }));

  const {
    countryList,
  } = action;

  const setPresetControl = (presetLists) => {
    // If we have a country list already
    if (!blank(countryList)) {
      // There are no available preset lists
      if (blank(presetLists)) {
        return setPresetClusterControl('CountryList', null);
      }
      const matchedPreset = presetLists.find(({ countryList: preset }) => equal(preset, countryList));
      // If our list matches a known preset list
      if (matchedPreset) {
        return setPresetClusterControl('CountryList', matchedPreset.name);
        // country list is custom list (/unknown preset)
      }
      return setPresetClusterControl('CountryList', null);
    }
    // else (No existing country list)
    // No known preset lists
    if (blank(presetLists)) {
      return setPresetClusterControl('CountryList', null, null);
    }
    const preset = presetLists.find(({ name }) => name === 'default') || presetLists[0];
    return setPresetClusterControl('CountryList', preset.name, preset.countryList);
  };

  yield put(setPresetControl(presetLists));

  yield put(storePresetClusterLists('CountryList', presetLists));
}

export function* getPresetBreakListSaga(action) {
  const {
    country,
    breakList,
  } = action;

  let {
    controlList,
    kidsWave,
  } = action;

  if (blank(controlList)) {
    controlList = yield select(makeSelectControlList());
  }

  if (blank(kidsWave)) {
    const CDF = yield select(makeSelectCDF());
    kidsWave = blank(CDF.Adults) ? false : !CDF.Adults;
  }

  const setPresetControl = (presetLists) => {
    // If we already have a break list
    if (!blank(breakList)) {
      // No preset lists are available
      if (blank(presetLists)) {
        return setPresetClusterControl('BreakList', null);
      }
      const matchingPreset = presetLists.find(({ breakList: preset }) => equal(breakList, preset));
      // If our break list matches a named preset
      if (matchingPreset) {
        return setPresetClusterControl('BreakList', matchingPreset.name);
        // Break list is custom break list (/unavailable preset)
      }
      return setPresetClusterControl('BreakList', null);
    }
    // else (no existing break list)
    // No preset lists are available
    if (blank(presetLists)) {
      return setPresetClusterControl('BreakList', null, null);
    }
    const preset = presetLists.find(({ name }) => name === 'default') || presetLists[0];
    return setPresetClusterControl('BreakList', preset.name, preset.breakList);
  };

  if (country && country !== 'None') {
    const data = yield graphQl(`
      {
        fnGetCountryBreaklists(countryName:"${country}"${kidsWave !== undefined ? `, kids:${kidsWave}` : ''}){
          nodes{
            countryBreaklists {
              nodes {
                name
                breaklistClusters {
                  nodes {
                    breaklistClustersBreakanswers {
                      nodes {
                        breakGroups {
                          nodes {
                            name
                          }
                        }
                        breakAnswers {
                          nodes {
                            name
                          }
                        }
                      }
                    }
                  }
                }
              }
            }
          }
        }
      }
    `);

    controlList = controlList.BreakList;

    const getBreakControlByName = (breakGroupName) => (
      /*
       * Find break group in control list where its name is equal to the passed breakGroupName
       * Break groups appear as an array of items in the form:
       * {[BREAK_NAME]: BreakGroupLabel, BreakAnswers}
       * We are comparing against BREAK_NAME
       */
      controlList.Options.AllSurveys.filter((bk) => Object.keys(bk)[0] === breakGroupName)[0]
    );

    const isBreakInBreakControlList = (breakGroupName) => {
      const breakFromControlList = getBreakControlByName(breakGroupName);
      return breakFromControlList !== undefined;
    };

    const getBreakListFromGQL = (namedList) => {
      const countrySpecificBreaks = getListOfCountrySpecificBreaks(controlList);

      const breakList = namedList.breaklistClusters.nodes.map((breaklistCluster) => breaklistCluster.breaklistClustersBreakanswers.nodes.map((breaklistClustersBreakanswer) => breaklistClustersBreakanswer.breakGroups.nodes.map((breakGroup, index) => ({
        [breakGroup.name]: breaklistClustersBreakanswer.breakAnswers.nodes[index].name,
      }))[0]).filter((breakGroup) => {
        const breakGroupName = Object.keys(breakGroup)[0];
        const inBreakControlList = isBreakInBreakControlList(breakGroupName);
        let availableForCountry = false;
        if (countrySpecificBreaks[breakGroupName] === undefined) {
          availableForCountry = true;
        } else {
          availableForCountry = countrySpecificBreaks[breakGroupName].includes(action.country);
        }
        return inBreakControlList && availableForCountry;
      }));

      return removeBlank(breakList);
    };

    const presetLists = data.fnGetCountryBreaklists.nodes[0].countryBreaklists.nodes.map((namedList) => ({
      name: namedList.name,
      breakList: getBreakListFromGQL(namedList),
    }));

    yield put(storePresetClusterLists('BreakList', presetLists));

    yield put(setPresetControl(presetLists));
  } else {
    yield put(storePresetClusterLists('BreakList', []));
    yield put(setPresetControl(undefined));
  }
}

/*
 * Polling function to call download URL and get return result through success or fail action
 */
export function* fetchPptxDownload(action) {
  const { cdf } = action;
  // eslint-disable-next-line no-constant-condition
  while (true) {
    const response = yield call(() => Fetch(PPTX_DOWNLOAD_URL, {
      method: 'post',
      headers: {
        Accept: 'application/json',
        'Content-Type': 'application/json',
      },
      body: JSON.stringify({
        cdf,
      }),
    }));
    const status = yield response.status;

    if (status === 500 || status === 503) {
      yield put(getPptxDownloadFailureAction(response));
    } else if (status === 200) {
      // Process the json response to ensure it has a URL
      const json = yield response.json();
      if (dig(json, ['success', 'pptx_url'])) {
        yield put(getPptxDownloadSuccessAction(json.success.pptx_url));
      }
    }

    yield call(delay, 1000);
  }
}

export default function* rootSaga() {
  yield [
    getQuestionSelections(),
    getPptxDownload(),
    makeQuestionControl(),
    makeTemporaryChartControl(),
    makeChartControl(),
    setNewChart(),
    fetchPresetCountryList(),
    fetchPresetBreakList(),
    isAuthenticated(),
    fetchExternalPackage(),
    fetchExternalUser(),
    fetchExternalCdf(),
  ];
}

function* getMetaJSON(questionCode) {
  const existingMeta = yield select(makeSelectMetaJSON());
  /* return empty meta object if we have a blank question */
  if (questionCode === '') {
    return {};
  }
  /* return cached meta if no change in question */
  if (dig(existingMeta, ['JSONQuestion', 'QuestionCodeAttributes', 'QuestionCode']) === questionCode) {
    return existingMeta;
  }
  let meta = yield graphQl(`
    {
      allQuestions(condition:{questionCode:"${questionCode}"}){
        nodes{
          metaJson
        }
      }
    }
  `);
  meta = dig(meta, ['allQuestions', 'nodes', 0, 'metaJson']);
  if (!blank(meta)) {
    meta = JSON.parse(meta);
  }
  yield put(storeMetaJSON(meta));
  return meta;
}

function* createChartControlList(visualisation, questionCode, kidsWave, questionMeta = null) {
  const clientPackage = yield select(makeSelectClientPackage());

  let meta = questionMeta;
  if (blank(meta)) {
    meta = yield select(makeSelectMetaData(questionCode));
  }
  if (blank(meta)) {
    meta = yield getMetaJSON(questionCode);
  }

  const ccc = new FFVis.ChartControlConfig(visualisation, questionCode, clientPackage, meta, kidsWave);
  const ccl = yield ccc.getControlList('promise');

  yield put(setMetaData(ccl.Metadata, questionCode));

  return ccl;
}

function* graphQl(queryString) {
  const response = yield Fetch(GRAPHQL_URL, {
    method: 'post',
    headers: {
      Accept: 'application/json',
      'Content-Type': 'application/json',
    },
    body: JSON.stringify({
      query: queryString,
    }),
  });
  const json = yield response.json();
  return json.data || { error: json.errors };
}

function* Fetch(url, options = {}) {
  // We need to set credentials to include so that requests properly include auth info
  setUnlessDefined(options, 'credentials', 'include');

  setUnlessDefined(options, 'headers', {});
  // Set origin for CORS (graphQl)
  setUnlessDefined(options.headers, 'Origin', `https://${AYDA_UI_HOSTNAME}`);

  const res = yield fetch(url, options);
  return yield res;
}



// WEBPACK FOOTER //
// ./app/containers/App/saga.js